import { useState } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import type { SkuUsage } from '@zilian/contracts';
import {
  ItemPartnerStats,
  PeriodSelect,
  usePartnerStats,
  type Period,
  type StatsSide,
} from './PartnerStats';
import { CountTabs } from './ui';

interface Props {
  sku: { id: string; code: string; name: string; spec?: string; unit: string; usage: SkuUsage };
  onClose: () => void;
}

/** One item's sales destinations (who buys it) and purchase sources (whom we buy it from). */
export default function ItemStatsDrawer({ sku, onClose }: Props) {
  const sells = sku.usage === 'SALE' || sku.usage === 'BOTH';
  const buys = sku.usage === 'PURCHASE' || sku.usage === 'BOTH';
  const [side, setSide] = useState<StatsSide>(sells ? 'SALE' : 'PURCHASE');
  const [period, setPeriod] = useState<Period>('YEAR');
  // Both usable sides load up front so each tab shows a real count.
  const sales = usePartnerStats('SALE', sku.id, period, sells);
  const purchases = usePartnerStats('PURCHASE', sku.id, period, buys);
  const current = side === 'SALE' ? sales : purchases;

  const tabs = [
    sells && { value: 'SALE' as const, label: '销售去向', count: sales.rows?.length ?? 0 },
    buys && { value: 'PURCHASE' as const, label: '采购来源', count: purchases.rows?.length ?? 0 },
  ].filter(Boolean) as { value: StatsSide; label: string; count: number }[];

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 860, maxWidth: '100vw' } } }}
    >
      {/* Same height as the app's top bar (border included) so the two line up, and pinned
          while the content below scrolls. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          height: 'var(--topbar-height)',
          flexShrink: 0,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }} noWrap>
            {sku.name}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }} noWrap>
            {/* The usage is already in the list row and decides the tabs, so it is not repeated. */}
            {[sku.code, sku.spec].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <IconButton aria-label="关闭" onClick={onClose} sx={{ mr: -1 }}>
          <CloseOutlined />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, display: 'flex', alignItems: 'center', gap: 2, minHeight: 64 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* A single side needs no switcher: it is just the section heading. */}
          {tabs.length > 1 ? (
            <CountTabs value={side} onChange={setSide} items={tabs} />
          ) : (
            <Typography sx={{ fontSize: 16, fontWeight: 650 }}>{tabs[0].label}</Typography>
          )}
        </Box>
        <PeriodSelect value={period} onChange={setPeriod} />
      </Box>
      <ItemPartnerStats side={side} rows={current.rows} error={current.error} unit={sku.unit} />
    </Drawer>
  );
}
